import GeneralEditorView from "./generalEditor.view";
import UnitsEditorView from "./unitsEditor.view";
import React, {useEffect, useRef, useState} from "react";
import BuildingsEditorView from "./buildingsEditor.view";
import LocalizationEditorView from "./localizationEditor.view";
import {HTML5Backend} from "react-dnd-html5-backend";
import {DndProvider} from "react-dnd";
import ImagesEditorView from "./imagesEditor.view";
import ArchiveView from "./archive.view";
import TechTreeEditorView from "../techTree/techTreeEditor.view";
import {useApp} from "../../app.context";
import {useIntegrator} from "../../integrator.context";
import SoundsEditorView from "./soundsEditor.view";
import TilesEditorView from "./tilesEditor.view";
import ResourcesEditor from "./resources.editor";
import {AccessLevel} from "../../models/accessLevel.model";
import WelcomeView from "./welcome.view";
import BuildingRewardsEditor from "./buildingRewards.editor";
import TechEditor from "./tech.editor";
import AttributesEditor from "./attributes.editor";
import SettingsEditorView from "./settingsEditor.view";
import SaveButton from "../../elements/saveButton";
import {SyncHiddenUnitAttributesCommand} from "../../commands/syncHiddenUnitAttributes.command";

enum EditorState {
    Welcome = "Welcome",
    General = "General",
    TechTree = "Tech Tree",
    Units = "Units",
    Buildings = "Buildings",
    Tiles = "Tiles",
    Resources = "Resources",
    Rewards = "City Rewards",
    Attributes = "Attributes",
    Tech = "Tech",
    Images = "Images",
    Sounds = "Sounds",
    Localization = "Localization",
    Archive = "Archive",
    CombatSimulation = "Combat Simulation",
    Settings = "Settings",
}

function EditorView() {
    const {totalChanges, branch, getBranchVersionNumber, goBack, goForward, canGoBack, canGoForward, executeCommand, state: integratorState} = useIntegrator();
    const {accessLevel, setLoading, setViewSettings, setViewBranch, exportRemoteConstants, setOnNavigateToLocalization, setOnNavigateToUnit, setOnNavigateToBuilding, attributeManifest} = useApp();

    // Auto-seed hidden unit attributes when both the manifest and a branch are
    // loaded. The command is idempotent — if every Hidden id is already in
    // state.unitAttributes it returns false and the editor stays clean. When
    // it does add entries, it goes through the normal command flow so the
    // branch shows as dirty until saved.
    //
    // Depends on state.unitAttributes (not the whole state) because in-place
    // mutations via executeCommand keep the array reference stable; only a
    // branch reload replaces it. That keeps this effect from re-running on
    // every unrelated edit.
    useEffect(() => {
        if (!attributeManifest) return;
        executeCommand(new SyncHiddenUnitAttributesCommand(attributeManifest));
    }, [attributeManifest, integratorState.unitAttributes]);
    const [state, setState] = useState<string>(loadCachedState());
    const [showProdConfirm, setShowProdConfirm] = useState(false);
    const isPopstateRef = useRef(false);

    useEffect(() => {
        if (totalChanges > 0) {
            window.onbeforeunload = () => true;
        } else {
            window.onbeforeunload = null;
        }
        return () => { window.onbeforeunload = null; };
    }, [totalChanges]);

    function selectState(newState: string) {
        if (newState === state) return;
        if (!isPopstateRef.current) {
            window.history.pushState({editorState: newState}, '');
        }
        setState(newState);
        cacheState(newState);
    }

    useEffect(() => {
        window.history.replaceState({editorState: state}, '');

        function onPopState(e: PopStateEvent) {
            const prev = e.state?.editorState;
            if (prev) {
                isPopstateRef.current = true;
                setState(prev);
                cacheState(prev);
                isPopstateRef.current = false;
            }
        }
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    useEffect(() => {
        setOnNavigateToLocalization((_key: string) => {
            selectState(EditorState.Localization);
        });
        setOnNavigateToUnit(() => {
            selectState(EditorState.Units);
        });
        setOnNavigateToBuilding(() => {
            selectState(EditorState.Buildings);
        });
        return () => {
            setOnNavigateToLocalization(undefined);
            setOnNavigateToUnit(undefined);
            setOnNavigateToBuilding(undefined);
        };
    }, [state]);

    async function exportData(prod: boolean) {
        if (prod) {
            setShowProdConfirm(true);
            return;
        }
        await doExport(false);
    }

    async function doExport(prod: boolean) {
        setLoading(true);
        await exportRemoteConstants(prod);
        setLoading(false);
    }

    return (
        <DndProvider backend={HTML5Backend}>
            <div className={'layout vertical'} style={{height: '100vh'}}>
                <div className={'layout horizontal'} style={{padding: 20, gap: 20, flexGrow: 1, minHeight: 0}}>
                    <div className={'layout vertical stretch'}>
                        <div className={'layout vertical center'} style={{gap: 10}}>
                            <p className={'text header'} style={{width: '100%', marginBottom: 10}}>Rift Editor</p>
                            <TitleText text={'Welcome'} icon={'fa-solid fa-hand-wave'}/>
                            <div className={'layout vertical gap'} style={{width: '100%'}}>
                                <StateButton state={EditorState.Welcome} file={undefined} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.General} file={'general.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Settings} file={undefined} selectedState={state}
                                             selectState={selectState}/>
                            </div>
                            <TitleText text={'Gameplay'} icon={'fa-solid fa-gamepad'}/>
                            <div className={'layout vertical gap'} style={{width: '100%'}}>
                                <StateButton state={EditorState.TechTree} file={'tech_tree.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Units} file={'units.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Buildings} file={'buildings.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Tiles} file={'tiles.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Resources} file={'resources.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Rewards} file={'rewards.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Attributes} file={'metadata.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Tech} file={'metadata.md'} selectedState={state}
                                             selectState={selectState}/>
                            </div>
                            <TitleText text={'Assets'} icon={'fa-solid fa-image'}/>
                            <div className={'layout vertical gap'} style={{width: '100%'}}>
                                <StateButton state={EditorState.Images} file={'images.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Sounds} file={'sounds.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Localization} file={'localization.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Archive} file={'archive.md'} selectedState={state}
                                             selectState={selectState}/>
                            </div>
                        </div>
                        <div className={'layout vertical gap'}>
                            <div className={'layout horizontal gap'}>
                                <button onClick={goBack} style={{opacity: `${canGoBack() ? 1 : 0.5}`}}>
                                    <span className={'fa-solid fa-undo'}/>
                                </button>
                                <button onClick={goForward} style={{opacity: `${canGoForward() ? 1 : 0.5}`}}>
                                    <span className={'fa-solid fa-redo'}/>
                                </button>
                                {accessLevel > AccessLevel.Guest && <button className={'flex-grow'} onClick={() => setViewBranch(true)}>
                                    {branch}
                                </button>}
                                <button onClick={() => setViewSettings(true)}
                                        className={'layout vertical center'}>{AccessLevel[accessLevel]}
                                </button>
                            </div>
                            {(accessLevel === AccessLevel.Admin || (accessLevel === AccessLevel.Tester && branch !== "main")) &&
                                <>
                                    <div className={'layout horizontal stretch'}>
                                        <p className={totalChanges > 0 ? 'warning-text' : 'muted-text'}>{totalChanges > 0 ? `Unsaved` : `No Changes`}</p>
                                        <p className={'normal-text'}>{`Version ${getBranchVersionNumber(branch)}`}</p>
                                    </div>
                                    <button className={`export-button ${totalChanges > 0 && 'active'}`}
                                            onClick={() => exportData(false)}>
                                        Export (dev,test)
                                    </button>
                                    <button className={`export-button production`}
                                            onClick={() => exportData(true)}>
                                        Export (staging,live)
                                    </button>
                                    <SaveButton/>
                                </>
                            }
                        </div>
                    </div>
                    <div style={{display: state === EditorState.Welcome ? 'contents' : 'none'}}><WelcomeView/></div>
                    <div style={{display: state === EditorState.General ? 'contents' : 'none'}}><GeneralEditorView/></div>
                    <div style={{display: state === EditorState.TechTree ? 'contents' : 'none'}}><TechTreeEditorView/></div>
                    <div style={{display: state === EditorState.Units ? 'contents' : 'none'}}><UnitsEditorView/></div>
                    <div style={{display: state === EditorState.Buildings ? 'contents' : 'none'}}><BuildingsEditorView/></div>
                    <div style={{display: state === EditorState.Tiles ? 'contents' : 'none'}}><TilesEditorView/></div>
                    <div style={{display: state === EditorState.Resources ? 'contents' : 'none'}}><ResourcesEditor/></div>
                    <div style={{display: state === EditorState.Rewards ? 'contents' : 'none'}}><BuildingRewardsEditor/></div>
                    <div style={{display: state === EditorState.Attributes ? 'contents' : 'none'}}><AttributesEditor/></div>
                    <div style={{display: state === EditorState.Tech ? 'contents' : 'none'}}><TechEditor/></div>
                    <div style={{display: state === EditorState.Images ? 'contents' : 'none'}}><ImagesEditorView/></div>
                    <div style={{display: state === EditorState.Sounds ? 'contents' : 'none'}}><SoundsEditorView/></div>
                    <div style={{display: state === EditorState.Localization ? 'contents' : 'none'}}><LocalizationEditorView/></div>
                    <div style={{display: state === EditorState.Archive ? 'contents' : 'none'}}><ArchiveView/></div>
                    <div style={{display: state === EditorState.Settings ? 'contents' : 'none'}}><SettingsEditorView/></div>
                </div>
            </div>
            {showProdConfirm && <div className={'layout vertical overlay'} onClick={() => setShowProdConfirm(false)}>
                <div className={'layout vertical gap box'} style={{padding: 30, gap: 20, maxWidth: 500}} onClick={e => e.stopPropagation()}>
                    <p className={'text header'}>Export to Production</p>
                    <p>{`Are you sure you want to export ${branch} v${getBranchVersionNumber(branch)} to Production? This will impact the Live Service...`}</p>
                    <div className={'layout horizontal gap'} style={{justifyContent: 'flex-end'}}>
                        <button onClick={() => setShowProdConfirm(false)}>Cancel</button>
                        <button className={'export-button production'} onClick={async () => {
                            setShowProdConfirm(false);
                            await doExport(true);
                        }}>Yes, Export it</button>
                    </div>
                </div>
            </div>}
        </DndProvider>
    )
}

function StateButton(props: {state: EditorState, file: string | undefined, selectedState: string, selectState(state: string): void}) {
    const {setMarkdownFile} = useApp();
    const selected = props.state === props.selectedState;

    return <button
                   className={`layout horizontal center stretch gap bar-state ${selected ? 'bar-state-active' : 'bar-state-inactive'}`}
                   style={{width: '100%'}}
                   onClick={() => {
                       if (selected && props.file) {
                           setMarkdownFile(props.file);
                       } else {
                           props.selectState(props.state);
                       }
                   }}>
        {props.state}
        {selected && props.file &&
            <span className={'fa-solid fa-square-info'}/>
        }
    </button>
}

function TitleText(props: {text: string, icon: string}) {
    return <p className={'layout horizontal'} style={{width: '100%', fontWeight: '600', gap: 10, alignItems: 'center'}}>
        <span className={props.icon}/>
        {props.text}
    </p>
}

function loadCachedState(): string {
    const value = localStorage.getItem('features-state');
    if (value === null) return EditorState.Welcome;
    // Tab was renamed from "Rewards" to "City Rewards"; migrate cached value
    // so users mid-session don't land on a no-op string.
    if (value === "Rewards") return EditorState.Rewards;
    return value;
}

function cacheState(state: string) {
    localStorage.setItem('features-state', state);
}

export default EditorView;